import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { throttle } from 'throttle-debounce';

import { basketActions, selectBasketScreen, selectCurrentCart, selectDeletingBaskets, selectDeletingCarts, selectLoadingEditBasket, useCurrentCart, useCurrentCarts, useReloadCarts } from '../../app/basket-slice';
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { useMessages2 } from '../../hooks/useLanguage';
import { CartsPanel } from '../../panels/carts-panel/carts-panel';
import { Modal, ModalContent, ModalFooter, ModalTitle, ModalTop } from '../modal/modal';
import { CartPanel, CartTop } from '../../panels/cart-panel/cart-panel';
import { buttonStyle } from '../buttons';
import { urls } from '../../navigation';
import { appContextSelector, useURLLang } from '../../app/main-slice';
import { SaveState } from '../../app/types/api-types';

export function BasketsModal() {

    const dispatch = useAppDispatch();
    const screen = useAppSelector(selectBasketScreen);

    const reloadCarts = useReloadCarts();

    const onClose = () => {
        // First basketActions.onClose() which also forces to not use cache?
        dispatch(basketActions.onClose());
        // Trigger reload of carts to reset the "editing" cart in back-end
        reloadCarts();
    }

    return <Modal onClose={onClose}>
        { screen === 'carts' ? <CartsModal/> : <CartModal/>  }
    </Modal>
}


function CartsModal() {

    const dispatch = useAppDispatch();    
    const navigate = useNavigate();
    const { messages } = useMessages2();
    const deletingCarts = useAppSelector(selectDeletingCarts);
    const reloadCarts = useReloadCarts();
    const langId = useURLLang();

    const { data: cartsList, isFetching, isLoading, isError } = useCurrentCarts();

    const onBack = () => {
        dispatch(basketActions.onCartsBack());
    }

    const onStartNewConfiguration = async () => {
        await dispatch(basketActions.startNewConfiguration());
        navigate(urls.home(langId));        
    }

    const onEditBasket = (id:number) => {
        dispatch(basketActions.editCart(id));
    }

    const onRemoveBasket = (id:number) => {
        dispatch(basketActions.deleteCart(id));        
    }

    const title = <ModalTop><ModalTitle>{ messages?.baskets_dialog_title ?? '' }</ModalTitle></ModalTop>;
    const footer = <ModalFooter>
        <button className={buttonStyle} onClick={onBack}>{ messages?.basket_back ?? '' }</button>
        &nbsp;
        <button className={buttonStyle} onClick={onStartNewConfiguration}>{ messages?.baskets_start_new_config ?? '' }</button>
    </ModalFooter>;

    if(isLoading) {
        return <>
            { title }
            <ModalContent>
                <span>{messages?.baskets_loading ?? ''}</span>
            </ModalContent>
            { footer }
        </>
    } else if(isError) {
        return <>
            { title }
            <ModalContent>
                <p>{messages?.baskets_load_error ?? ''}</p>
                <p>
                    <button className={buttonStyle} onClick={reloadCarts}>{messages?.baskets_try_again}</button>
                </p>
            </ModalContent>
            { footer }
        </>
    } else {
        return <>
            { title }
            <ModalContent>
                <CartsPanel cartList={cartsList ?? []}
                            disabled={deletingCarts.length > 0 || isFetching}
                            deleting={deletingCarts}
                            onEditCart={onEditBasket}
                            onRemoveCart={onRemoveBasket}
                            messages={ messages ?? {} } />
            </ModalContent>
            { footer }
        </>
    }
}


function CartModal() {

    const dispatch = useAppDispatch();
    const { messages } = useMessages2();
    const cartId = useAppSelector(selectCurrentCart);
    const loadingEditBasket = useAppSelector(selectLoadingEditBasket);
    const deletingBaskets = useAppSelector(selectDeletingBaskets);
    const { data: cart, isSuccess } = useCurrentCart();
    const [ editName, setEditName ] = useState<string|undefined>(undefined);
    const navigate = useNavigate();
    const langId = useURLLang();

    const appContext = useAppSelector(appContextSelector);


    if(isSuccess && editName === undefined) {
        setEditName(cart.name);
    }

    const onBack = () => {
        dispatch(basketActions.onEditBack());
    }

    const onBasketsOverview = () => {
        dispatch(basketActions.onBasketsOverview());
    }

    const onAddProduct = () => {
        if(cartId) {
            dispatch(basketActions.startAddProduct(cartId));
            navigate(urls.home(langId));
        }
    }

    const onEmptyCart = () => {
        if(cart && cartId) {
            dispatch(basketActions.deleteBaskets({
                cartId,
                basketIds: cart.baskets.map(b => b.shoppingbasketID)
            }));    
        }
    }

    const onPrintCart = async () => {
        if (!cartId) return;

        const printCartCall = async () => {
            const result = await dispatch(basketActions.printShoppingBasket(cartId));
            const pdfURL = result.payload as string | undefined;
            return pdfURL
        }

        if (appContext == 'stapp') {
            const pdfURL = await printCartCall()
            const pdfWindow = window.open(pdfURL, '_blank');
            if (pdfWindow) {
                pdfWindow.onload = () => {
                    pdfWindow.focus();
                    pdfWindow.print();
                };
            }
        }
        else {
            // Open a blank window immediately in response to user interaction
            // Pop-up block workaround
            const pdfWindow = window.open('', '_blank');
            if (!pdfWindow) return;

            try {
                const pdfURL = await printCartCall()
                if (pdfURL) {
                    // Set the URL of the previously opened window
                    pdfWindow.location.href = pdfURL;

                    pdfWindow.onload = () => {
                        pdfWindow.focus();
                        pdfWindow.print();
                    };
                } else {
                    pdfWindow.close();
                }
            } catch (error) {
                pdfWindow.close();
            }
        }
    };


    const onCart3D = () => {
        if(cartId) {
            navigate(urls.cart3d(langId, cartId))
        }
    }

    const onEdit = async (basketId: number) => {
        if(cartId) {
            const result = await dispatch(basketActions.editBasket({ cartId, basketId }));
            const savedState = result.payload as SaveState;        
            if(savedState.variant) {
                navigate(urls.variantCfg(langId, savedState.category, savedState.family, savedState.variant));
            } else {
                navigate(urls.familyCfg(langId, savedState.category, savedState.family));
            }            
        }
    };
    
    const onRemove = (basketId: number) => {
        if(cartId) {
            dispatch(basketActions.deleteBaskets({ cartId, basketIds: [ basketId ] }));
        }
    };
    
    const onQuantityChanged = (basketId: number, quantity: number) => {
        if(cartId) {
            dispatch(basketActions.updateBasketQuantity({ cartId, basketId, quantity }));
        }
    };

    const dispatchNameReference = useCallback(throttle(
        1000,
        (name: string, description: string) => {
            dispatch(basketActions.updateCartNameAndDescription({name, description}));
        },
        { noLeading: true, noTrailing: false }
    ), [ dispatch ]);

    const onNameChanged = (v:string) => {
        dispatchNameReference(v, cart?.description ?? '');
        setEditName(v);
    }

    const footer = <ModalFooter>
        <button className={buttonStyle} onClick={onBack}>{ messages?.basket_back ?? '' }</button>
    </ModalFooter>

    if(cart && messages) {
        return <>
            <ModalTop>
                <ModalTitle>
                    <CartTop name={editName ?? ''} messages={messages} onNameChanged={onNameChanged}/>
                </ModalTitle>
            </ModalTop>
            <ModalContent>
                <CartPanel cart={cart}
                        //cartPDFURL={cart.pdfURL}
                        totalPrice={cart.amount}
                        totalPriceCurrency={cart.currencyCode}
                        disabled={loadingEditBasket}
                        deletingBaskets={deletingBaskets}
                        messages={ messages }
                        onAddProduct={onAddProduct}
                        onEmptyCart={onEmptyCart}
                        onPrintCart={onPrintCart}
                        onCart3D={onCart3D}
                        onBasketsOverview={onBasketsOverview}
                        onSave={onBasketsOverview}
                        onEdit={onEdit}
                        onRemove={onRemove}
                        onQuantityChanged={onQuantityChanged}
                        />
            </ModalContent>
            { footer }
        </>
    } else {
        return <>
            <ModalContent>
                <span>Loading...</span>
            </ModalContent>
            { footer }
        </>
    }


}